import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, IconButton, Chip, Grid, Select, MenuItem, FormControl, InputLabel,
  Avatar, Stack, Divider, Tooltip, CardHeader, Snackbar, LinearProgress, Switch, FormControlLabel,
  Tabs, Tab
} from '@mui/material';
import {
  Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Close as CloseIcon,
  Category as CategoryIcon, Build as BuildIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useBusiness } from '../contexts/BusinessContext';
import ServiceGeneratorWizard from './ServiceGeneratorWizard';

const ServiceManager = () => {
  const { locationId: urlLocationId } = useParams();
  const { selectedBusiness } = useBusiness();
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openGeneratorWizard, setOpenGeneratorWizard] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingServiceName, setEditingServiceName] = useState('');  // Store service name for AI suggestions
  const [aiSuggestion, setAiSuggestion] = useState('');  // AI-generated suggestion
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [serviceData, setServiceData] = useState({
    type: 'structured',
    serviceTypeId: '',
    category: '',
    displayName: '',
    description: ''
  });
  const [editDescription, setEditDescription] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteServiceData, setDeleteServiceData] = useState({ index: null, type: 'freeForm', name: '', service: null });
  const [serviceTracking, setServiceTracking] = useState({}); // Track which services have createdAt timestamps
  const [addServiceActivityDate, setAddServiceActivityDate] = useState(false);
  const [serviceActivityDate, setServiceActivityDate] = useState('');
  const [scheduledServices, setScheduledServices] = useState([]);
  const [schedulingInProgress, setSchedulingInProgress] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0=Tracked, 1=Not Tracked, 2=Scheduled

  const { freeFormServiceItems = [], structuredServiceItems = [], categories = [], serviceTypes = [], serviceActivity = [] } = services;

  // Extract unique categories from existing free-form services
  const availableFreeFormCategories = [...new Set(categories.map(item => item.name))].sort();

  // Extract used structured service type IDs
  const usedStructuredServiceTypeIds = new Set(structuredServiceItems.map(item => item.serviceTypeId));

  // Get all available service types from backend (from categories)
  const allAvailableServiceTypes = serviceTypes.filter(st => !usedStructuredServiceTypeIds.has(st.serviceTypeId));

  // Format category display name (convert 'categories/gcid:gutter_service' to 'Gutter Service')
  const formatCategoryName = (categoryPath) => {
    // Extract the gcid part if it's a full path
    let category = categoryPath;
    if (categoryPath.includes(':')) {
      category = categoryPath.split(':')[1];
    }
    // Convert gutter_service to Gutter Service
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Build combined categories dropdown with used freeForm categories and unused structured types
  const categoriesForDropdown = [
    // Used free-form categories (can add multiple services)
    ...availableFreeFormCategories.map(cat => ({
      value: cat,
      label: formatCategoryName(cat),
      type: 'freeForm',
      isUsed: true
    })),
    // Unused structured service types (one service per type)
    ...allAvailableServiceTypes.map(service => ({
      value: service.serviceTypeId,
      label: service.displayName || formatCategoryName(service.serviceTypeId),
      type: 'structured',
      isUsed: false
    }))
  ];

  // Count services by category
  const servicesByCategory = {};

  freeFormServiceItems.forEach(item => {
    const categoryName = getServiceDisplayName(item.category);
    servicesByCategory[categoryName] = (servicesByCategory[categoryName] || 0) + 1;
  });

  // Fetch services for selected business or URL location
  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use URL locationId if available, otherwise fall back to selectedBusiness
      let locationId = urlLocationId;

      if (!locationId) {
        if (!selectedBusiness?.id && !selectedBusiness?.googleLocationId) {
          setError('No business selected');
          setLoading(false);
          return;
        }
        locationId = selectedBusiness.googleLocationId || selectedBusiness.id;
      }

      if (!locationId) {
        setError('Invalid business location ID');
        setLoading(false);
        return;
      }

      console.log(`📍 Fetching services for location: ${locationId}`);

      const response = await fetch(`/api/services/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.status}`);
      }

      const resp = await response.json();

      console.log('✅ Services fetched:', resp);
      setServices(resp.data || {});
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [urlLocationId, selectedBusiness]);

  // Fetch scheduled services
  const fetchScheduledServices = useCallback(async () => {
    try {
      let locationId = urlLocationId;
      if (!locationId) {
        locationId = selectedBusiness?.googleLocationId || selectedBusiness?.id;
      }

      if (!locationId) return;

      const response = await fetch(`/api/services/${locationId}/scheduled`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const resp = await response.json();
        setScheduledServices(resp.data || []);
        console.log('✅ Scheduled services fetched:', resp.data);
      }
    } catch (err) {
      console.error('Error fetching scheduled services:', err);
    }
  }, [urlLocationId, selectedBusiness]);

  // Handle Schedule Services button click
  const handleScheduleServices = async () => {
    let locationId;
    try {
      setSchedulingInProgress(true);
      setError(null);

      locationId = selectedBusiness.googleLocationId || selectedBusiness.id;
      const timestamp = new Date().toISOString();

      if (!locationId) {
        setError('Invalid business location ID');
        setSchedulingInProgress(false);
        return;
      }

      // Frontend Logging
      console.log(`\n${'='.repeat(80)}`);
      console.log(`🎯 [SCHEDULE SERVICES] Button Clicked - Frontend`);
      console.log(`${'='.repeat(80)}`);
      console.log(`📅 Timestamp: ${timestamp}`);
      console.log(`📍 Location ID: ${locationId}`);
      console.log(`🏢 Business: ${selectedBusiness.businessName || 'Unknown'}`);
      console.log(`🎯 Schedule Services clicked, starting background process...`);

      const response = await fetch(`/api/services/${locationId}/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scheduledPostDate: null })
      });

      if (response.ok) {
        const result = await response.json();

        // Log Success Response
        console.log(`\n✅ SUCCESS RESPONSE RECEIVED:`);
        console.log(`   Scheduled Count: ${result.scheduledCount}`);
        if (result.metadata) {
          console.log(`   Keywords Used: ${result.metadata.keywordsCount}`);
          console.log(`   Categories Used: ${result.metadata.categoriesCount}`);
          console.log(`   Services Generated: ${result.metadata.generatedCount}`);
          console.log(`   Total Scheduled: ${result.metadata.totalScheduledCount}`);
          console.log(`   Execution Time: ${result.metadata.executionTimeMs}ms`);
        }
        console.log(`${'='.repeat(80)}\n`);

        setSuccessMessage(`✅ ${result.scheduledCount} services scheduled successfully!`);
        fetchScheduledServices();
      } else {
        const errorData = await response.json();
        console.error(`❌ FAILED TO SCHEDULE SERVICES:`, errorData);
        console.error(`${'='.repeat(80)}\n`);
        setError(errorData.error || 'Failed to schedule services');
      }
    } catch (err) {
      console.error(`\n❌ FRONTEND ERROR:`);
      console.error(`   Error: ${err.message}`);
      console.error(`   Location ID: ${locationId}`);
      console.error(`${'='.repeat(80)}\n`);
      setError(err.message || 'Failed to schedule services');
    } finally {
      setSchedulingInProgress(false);
    }
  };

  // Publish a scheduled service
  const handlePublishScheduledService = async (serviceId) => {
    try {
      setLoading(true);
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      const response = await fetch(`/api/services/${locationId}/scheduled/${serviceId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Service published successfully!');
        fetchServices();
        fetchScheduledServices();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to publish service');
      }
    } catch (err) {
      console.error('Error publishing scheduled service:', err);
      setError(err.message || 'Failed to publish service');
    } finally {
      setLoading(false);
    }
  };

  // Delete a scheduled service
  const handleDeleteScheduledService = async (serviceId) => {
    try {
      setLoading(true);
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      const response = await fetch(`/api/services/${locationId}/scheduled/${serviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Scheduled service deleted');
        fetchScheduledServices();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete scheduled service');
      }
    } catch (err) {
      console.error('Error deleting scheduled service:', err);
      setError(err.message || 'Failed to delete scheduled service');
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity tracking data to see which services have createdAt timestamps
  const fetchServiceTracking = useCallback(async () => {
    try {
      let locationId = urlLocationId;
      if (!locationId) {
        locationId = selectedBusiness?.googleLocationId || selectedBusiness?.id;
      }

      if (!locationId) return;

      const response = await fetch(`/api/activities/${locationId}?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) return;

      const resp = await response.json();
      const activities = resp.data || [];

      // Build a map of service names to their tracking status (whether they have createdAt)
      const tracking = {};
      activities.forEach(activity => {
        if (activity.activityType === 'service_added' && activity.serviceName) {
          tracking[activity.serviceName] = {
            hasCreatedAt: !!activity.createdAt,
            createdAt: activity.createdAt
          };
        }
      });

      setServiceTracking(tracking);
    } catch (err) {
      console.error('Error fetching activity tracking:', err);
    }
  }, [urlLocationId, selectedBusiness]);

  // Load services on component mount or when business/URL changes
  useEffect(() => {
    if (urlLocationId || selectedBusiness?.id || selectedBusiness?.googleLocationId) {
      setSelectedCategory(null); // Reset category filter when business/location changes
      fetchServices();
      fetchServiceTracking();
      fetchScheduledServices();
    }
  }, [urlLocationId, selectedBusiness, fetchServices, fetchServiceTracking, fetchScheduledServices]);

  // Create new service
  const createService = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      const description = (serviceData.description || '').trim();
      let servicePayload;
      let requestPayload;

      if (serviceData.type === 'freeForm') {
        // Validate free-form service
        if (!serviceData.category || !serviceData.displayName) {
          throw new Error('Please fill in all required fields for free-form service');
        }

        servicePayload = {
          category: serviceData.category,
          label: {
            displayName: serviceData.displayName,
            ...(description && { description })
          }
        };

        requestPayload = {
          type: 'freeForm',
          serviceData: servicePayload
        };
      } else {
        // Validate structured service
        if (!serviceData.serviceTypeId) {
          throw new Error('Please select a service type');
        }

        servicePayload = {
          serviceTypeId: serviceData.serviceTypeId,
          ...(description && { description })
        };

        requestPayload = {
          type: 'structured',
          serviceData: servicePayload
        };
      }

      // Call API to create service
      const response = await fetch(`/api/services/${locationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to create service: ${response.status}`);
      }

      setSuccessMessage('Service created successfully!');
      setOpenAddDialog(false);

      // Reset form
      setServiceData({
        type: 'freeForm',
        serviceTypeId: '',
        category: '',
        displayName: '',
        description: ''
      });

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error creating service:', err);
      setError(err.message || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation dialog
  const handleOpenDeleteDialog = (serviceIndex, serviceType = 'freeForm', serviceName = '', serviceObject = null) => {
    setDeleteServiceData({ index: serviceIndex, type: serviceType, name: serviceName, service: serviceObject });
    setOpenDeleteDialog(true);
  };

  // Confirm and delete service
  const handleConfirmDelete = async () => {
    if (deleteServiceData.index === null) {
      return;
    }
    setOpenDeleteDialog(false);
    await deleteService(deleteServiceData.index, deleteServiceData.type, deleteServiceData.service);
    setDeleteServiceData({ index: null, type: 'freeForm', name: '', service: null });
  };

  // Delete a service
  const deleteService = async (serviceIndex, serviceType = 'freeForm', serviceObject = null) => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      // Find the actual index in the full services array (not the filtered view index)
      let actualIndex = serviceIndex;

      if (serviceObject) {
        if (serviceType === 'freeForm') {
          // Find the actual index of this service in the full freeFormServiceItems array
          actualIndex = freeFormServiceItems.findIndex(item =>
            item?.label?.displayName === serviceObject?.label?.displayName &&
            item?.category === serviceObject?.category
          );
        } else if (serviceType === 'structured') {
          // Find the actual index of this structured service
          actualIndex = structuredServiceItems.findIndex(item =>
            item?.serviceTypeId === serviceObject?.serviceTypeId
          );
        }

        if (actualIndex === -1) {
          throw new Error('Service not found in current list');
        }
      }

      const response = await fetch(`/api/services/${locationId}/${actualIndex}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serviceType })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to delete service: ${response.status}`);
      }

      const result = await response.json();

      setSuccessMessage('Service deleted successfully!');

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err.message || 'Failed to delete service');
    } finally {
      setLoading(false);
    }
  };

  // Edit service description
  const handleOpenEditDialog = (serviceIndex, currentDescription, serviceName = '', serviceType = 'freeForm') => {
    setEditingServiceIndex(serviceIndex);
    setEditingServiceType(serviceType);
    setEditDescription(currentDescription || '');
    setEditingServiceName(serviceName || '');
    setAiSuggestion('');
    setAddServiceActivityDate(false);
    setServiceActivityDate('');
    setOpenEditDialog(true);

    // Generate AI suggestion if service name is provided
    if (serviceName) {
      generateAiDescriptionSuggestion(serviceName);
    }
  };

  // Generate AI description suggestion
  const generateAiDescriptionSuggestion = async (serviceName) => {
    try {
      setLoadingSuggestion(true);
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      const response = await fetch(`/api/services/${locationId}/generate-description`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serviceName })
      });

      if (response.ok) {
        const data = await response.json();
        setAiSuggestion(data.description || '');
        console.log('✅ AI suggestion generated:', data.description);
      } else {
        console.error('Failed to generate suggestion:', response.status);
      }
    } catch (err) {
      console.error('Error generating AI suggestion:', err);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  // Save edited service description
  const saveEditedDescription = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      if (!locationId) {
        throw new Error('Invalid business location ID');
      }

      if (editingServiceIndex === null) {
        throw new Error('No service selected for editing');
      }

      const response = await fetch(`/api/services/${locationId}/${editingServiceIndex}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: editDescription.trim(),
          serviceType: editingServiceType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `Failed to update service: ${response.status}`);
      }

      // If user wants to add a creation date to service activity, save it
      if (addServiceActivityDate && serviceActivityDate && editingServiceName) {
        try {
          const activityResponse = await fetch(`/api/activities/${locationId}/add-service-creation-date`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              serviceName: editingServiceName,
              createdAt: serviceActivityDate
            })
          });

          if (!activityResponse.ok) {
            console.error('Failed to add service creation date to activity');
          } else {
            console.log('✅ Service creation date added to activity');
            // Refresh the tracking data
            await fetchServiceTracking();
          }
        } catch (err) {
          console.error('Error adding service to activity:', err);
        }
      }

      setSuccessMessage('Service updated successfully!');
      setOpenEditDialog(false);
      setEditingServiceIndex(null);
      setEditDescription('');
      setAddServiceActivityDate(false);
      setServiceActivityDate('');

      // Refresh services list
      fetchServices();

    } catch (err) {
      console.error('Error updating service:', err);
      setError(err.message || 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const hasServices = freeFormServiceItems?.length > 0 || structuredServiceItems?.length > 0;

  const formatServiceName = (inputString) => {
    const match = inputString.match(/(?:[:/])([a-zA-Z_]+)$/);
    let lastPart = match ? match[1] : inputString;
    return lastPart
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  if (!selectedBusiness?.id && !selectedBusiness?.googleLocationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
        Bitte wählen Sie ein Unternehmen aus der Dropdown-Liste, um dessen Services zu verwalten
        </Alert>
      </Box>
    );
  }

   function getServiceDisplayName(categoryName) {
  const category = categories.find(item => item.name === categoryName);
  return category ? category.displayName : null; // or return '' or throw error
}

function getDefinedServiceDisplayName(categoryName) {
  const category = serviceTypes.find(item => item.serviceTypeId === categoryName);
  return category ? category.displayName : null; // or return '' or throw error
}


const formatDisplayName = (id) =>
  id ? id.replace(/^job_type_id:/, '')
         .replace(/_/g, ' ')
         .replace(/\b\w/g, char => char.toUpperCase()) : '';

// Get unique dates from serviceActivity createdAt field
const getAvailableDates = () => {
  const dates = new Set();

  serviceActivity.forEach(activity => {
    // Use createdAt if available, fall back to timestamp
    const dateToUse = activity.createdAt || activity.timestamp;
    if (dateToUse) {
      try {
        let dateStr;

        // Handle Date objects
        if (dateToUse instanceof Date) {
          const year = dateToUse.getFullYear();
          const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
          const day = String(dateToUse.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        }
        // Check if it's ISO format: YYYY-MM-DDTHH:MM:SS
        else if (typeof dateToUse === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(dateToUse)) {
          // ISO format: "2026-09-18T13:58:45.346Z"
          dateStr = dateToUse.split('T')[0]; // YYYY-MM-DD
        } else if (typeof dateToUse === 'string') {
          // String format: "Fri Sep 18 2026 15:58:45 GMT+0200..."
          const parsed = new Date(dateToUse);
          if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            dateStr = `${year}-${month}-${day}`;
          }
        }

        if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateStr.split('-');
          const deDate = `${day}.${month}.${year}`; // dd.mm.yyyy
          dates.add(deDate);
        }
      } catch (e) {
        console.warn('Failed to parse date from serviceActivity:', activity.createdAt);
      }
    }
  });

  return Array.from(dates).sort((a, b) => new Date(b.split('.').reverse().join('-')) - new Date(a.split('.').reverse().join('-')));
};

// Filter services based on selected category and date
const getFilteredFreeFormServices = () => {
  let filtered = freeFormServiceItems;

  // Filter by category
  if (selectedCategory) {
    filtered = filtered.filter(service => {
      const categoryName = getServiceDisplayName(service?.category);
      return categoryName === selectedCategory;
    });
  }

  // Filter by date (without timezone conversion)
  // ONLY show services with timestamps when a date filter is applied
  if (selectedDate) {
    filtered = filtered.filter(service => {
      const serviceName = service?.label?.displayName || '';
      const serviceCategory = service?.category || '';

      // Find matching activity in serviceActivity array
      const activity = serviceActivity.find(act =>
        act.serviceName === serviceName && act.serviceCategory === serviceCategory && act.activityType === 'service_added'
      );

      // Use createdAt if available, fall back to timestamp
      const dateToUse = activity?.createdAt || activity?.timestamp;
      if (!activity || !dateToUse) return false;

      try {
        let dateStr;

        // Handle Date objects
        if (dateToUse instanceof Date) {
          const year = dateToUse.getFullYear();
          const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
          const day = String(dateToUse.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        }
        // Check if it's ISO format: YYYY-MM-DDTHH:MM:SS
        else if (typeof dateToUse === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(dateToUse)) {
          // ISO format: "2026-09-18T13:58:45.346Z"
          dateStr = dateToUse.split('T')[0]; // YYYY-MM-DD
        } else if (typeof dateToUse === 'string') {
          // String format: "Fri Sep 18 2026 15:58:45 GMT+0200..."
          const parsed = new Date(dateToUse);
          if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            dateStr = `${year}-${month}-${day}`;
          }
        }

        if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return false;
        const [year, month, day] = dateStr.split('-');
        const deDate = `${day}.${month}.${year}`; // dd.mm.yyyy
        return deDate === selectedDate;
      } catch (e) {
        console.warn('Failed to parse date:', dateToUse);
        return false;
      }
    });

    console.log(`✅ FILTERED RESULTS FOR ${selectedDate}:`);
    console.log(`   COUNT: ${filtered.length}`);
    filtered.forEach((service, idx) => {
      const serviceName = service?.label?.displayName || '';
      const serviceCategory = service?.category || '';
      const activity = serviceActivity.find(act =>
        act.serviceName === serviceName && act.serviceCategory === serviceCategory && act.activityType === 'service_added'
      );
      const dateToUse = activity?.createdAt || activity?.timestamp;
      console.log(`   [${idx}] ${serviceName} | ${dateToUse}`);
    });
  }

  return filtered;
};

// Get tracked services (services with timestamps)
const getTrackedServices = () => {
  return getFilteredFreeFormServices().filter(service => {
    const serviceName = service?.label?.displayName || '';
    const serviceCategory = service?.category || '';
    const activity = serviceActivity.find(act =>
      act.serviceName === serviceName && act.serviceCategory === serviceCategory && act.activityType === 'service_added'
    );
    const dateToUse = activity?.createdAt || activity?.timestamp || service?.timestamp;
    return !!dateToUse;
  });
};

// Get not tracked services (services without timestamps)
const getNotTrackedServices = () => {
  return getFilteredFreeFormServices().filter(service => {
    const serviceName = service?.label?.displayName || '';
    const serviceCategory = service?.category || '';
    const activity = serviceActivity.find(act =>
      act.serviceName === serviceName && act.serviceCategory === serviceCategory && act.activityType === 'service_added'
    );
    const dateToUse = activity?.createdAt || activity?.timestamp || service?.timestamp;
    return !dateToUse;
  });
};

// For structured services, we would need to map them to categories
// Since structured services are by serviceTypeId, we return all if no filter
const getFilteredStructuredServices = () => {
  if (!selectedCategory) return structuredServiceItems;
  // For now, return empty if filter is selected, as structured services are type-based
  // You can modify this if structured services should also be filterable by category
  return [];
};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <div>
              <Typography variant="h5" gutterBottom>
                Dienstleistungsverwaltung
              </Typography>

              <Typography variant="body2" color="textSecondary">
                {selectedBusiness.businessName || selectedBusiness.displayName}
              </Typography>
            </div>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => setOpenGeneratorWizard(true)}
                disabled={loading}
                sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45A049' } }}
              >
                Generate Services
              </Button>
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={handleScheduleServices}
                disabled={schedulingInProgress}
                sx={{ bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' } }}
              >
                {schedulingInProgress ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Schedule Services'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                disabled={loading}
              >
                Neuen Service hinzufügen
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Overview - Services by Category */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Services Card */}
        {freeFormServiceItems?.length > 0 && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card
              sx={{
                backgroundColor: selectedCategory === null ? 'primary.main' : '#E3F2FD',
                color: selectedCategory === null ? 'white' : 'inherit',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                },
                ...(selectedCategory === null && { boxShadow: 4 })
              }}
              onClick={() => setSelectedCategory(null)}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <CategoryIcon sx={{ fontSize: 40, mb: 1, color: selectedCategory === null ? 'white' : 'primary.main' }} />
                <Typography variant="h4" sx={{ color: selectedCategory === null ? 'white' : 'inherit', fontWeight: 'bold' }}>{freeFormServiceItems?.length || 0}</Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: selectedCategory === null ? 0.9 : 1 }}>
                  Gesamte individuelle Dienstleistungen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Services by Category */}
        {Object.entries(servicesByCategory).length > 0 ? (
          Object.entries(servicesByCategory).map(([category, count]) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: selectedCategory === category ? 'success.light' : 'inherit',
                  border: selectedCategory === category ? '2px solid' : '1px solid',
                  borderColor: selectedCategory === category ? 'success.main' : 'divider',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  },
                  ...(selectedCategory === category && { boxShadow: 4 })
                }}
                onClick={() => setSelectedCategory(category)}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <CategoryIcon
                    color={selectedCategory === category ? 'success' : 'success'}
                    sx={{ fontSize: 40, mb: 1 }}
                  />
                  <Typography variant="h4">{count}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {category}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Noch keine Services hinzugefügt
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Date Filter */}
      {freeFormServiceItems?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#F5F5F5' }}>
          <FormControl fullWidth sx={{ maxWidth: 450 }}>
            <InputLabel id="date-filter-label" shrink sx={{ color: '#d32f2f', fontWeight: 500, fontSize: '0.9rem' }}>Nach Erstellungsdatum filtern</InputLabel>
            <Select
              labelId="date-filter-label"
              id="date-filter-select"
              value={selectedDate || ''}
              onChange={(e) => setSelectedDate(e.target.value || null)}
              label="Nach Erstellungsdatum filtern"
              variant="outlined"
              displayEmpty
              notched
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d32f2f'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d32f2f'
                }
              }}
              renderValue={(value) =>
                value ? <strong>{value}</strong> : <em>Alle Daten</em>
              }
            >
              <MenuItem value="">
                <em>Alle Daten</em>
              </MenuItem>
              {getAvailableDates().map((date) => (
                <MenuItem key={date} value={date}>
                  {date}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedDate && (
            <Chip
              label={`Gefiltert: ${selectedDate}`}
              onDelete={() => setSelectedDate(null)}
              sx={{ ml: 2 }}
              color="success"
            />
          )}
        </Paper>
      )}

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Active Filter Indicator */}
      {selectedCategory && (
        <Alert
          severity="info"
          onClose={() => setSelectedCategory(null)}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setSelectedCategory(null)}
            >
              Clear Filter
            </Button>
          }
        >
          Showing services for category: <strong>{selectedCategory}</strong>
        </Alert>
      )}

      {/* Services Tables with Tabs */}
      {freeFormServiceItems?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CategoryIcon />
              <Typography variant="h6">Individuelle Dienstleistungen</Typography>
            </Stack>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: -1 }}
            >
              <Tab label={`Tracked (${getTrackedServices().length})`} />
              <Tab label={`Not Tracked (${getNotTrackedServices().length})`} />
              <Tab label={`Scheduled (${scheduledServices.length})`} />
            </Tabs>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Kategorie</strong></TableCell>
                  <TableCell><strong>Anzeigename</strong></TableCell>
                  <TableCell><strong>Beschreibung</strong></TableCell>
                  <TableCell><strong>Erstellt am</strong></TableCell>
                  <TableCell align="center"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(activeTab === 0 ? getTrackedServices() : activeTab === 1 ? getNotTrackedServices() : scheduledServices).map((service, index) => {
                  // Handle both regular services (with label) and scheduled services (with displayName directly)
                  const isScheduledService = !!service?.displayName && !service?.label;
                  const serviceName = isScheduledService ? service.displayName : service?.label?.displayName || '';
                  const serviceCategory = isScheduledService ? service.category : service?.category || '';

                  // Get the activity for this service from serviceActivity array
                  const activity = isScheduledService ? null : serviceActivity.find(act =>
                    act.serviceName === serviceName && act.serviceCategory === serviceCategory && act.activityType === 'service_added'
                  );

                  // Display the creation date from activity tracking if available, otherwise use activity timestamp or service timestamp
                  const displayDate = isScheduledService
                    ? service.scheduledAt
                    : (activity?.createdAt || activity?.timestamp || service?.timestamp);

                  // If either has a timestamp, it's tracked (blue). If neither has timestamp, it's not tracked (green)
                  const isTracked = !!displayDate;
                  const rowBgColor = isScheduledService ? '#fff3cd' : (isTracked ? '#e3f2fd' : '#c8e6c9');

                  const displayDateFormatted = displayDate ? new Date(displayDate).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '-';

                  return (
                  <TableRow key={`service-${activeTab}-${index}`} hover sx={{ backgroundColor: rowBgColor }}>
                    <TableCell>
                      <Chip
                        label={isScheduledService ? service.category : (getServiceDisplayName(service?.category) ||  formatDisplayName(service?.serviceTypeId))}
                        size="small"
                        color={isScheduledService ? "default" : "secondary"}
                        variant="outlined"
                        sx={{ backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{serviceName}</span>
                        {isScheduledService && (
                          <Tooltip title={`Scheduled - ${displayDateFormatted}`}>
                            <Chip
                              label="📅 Scheduled"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {!isScheduledService && isTracked && (
                          <Tooltip title={`Tracked - ${displayDate}`}>
                            <Chip
                              label="✅ Tracked"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {!isScheduledService && !isTracked && (
                          <Tooltip title="Not tracked - no date">
                            <Chip
                              label="❌ Not Tracked"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{isScheduledService ? service.description : (service?.label?.description || ' No description')}</TableCell>
                    <TableCell>
                      {displayDateFormatted}
                    </TableCell>
                    <TableCell align="center">
                      {isScheduledService ? (
                        <>
                          <Tooltip title="Publish this scheduled service">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handlePublishScheduledService(service._id)}
                              disabled={loading}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete this scheduled service">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteScheduledService(service._id)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(index, service?.label?.description, service?.label?.displayName || service?.displayName)}
                            disabled={loading}
                            title="Edit description"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(index, 'freeForm', service?.label?.displayName || service?.displayName || 'Unknown Service', service)}
                            disabled={loading}
                            title="Delete service"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Structured services table */}
      {structuredServiceItems?.length > 0 && !selectedCategory && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildIcon />
                <Typography variant="h6">Structured Services</Typography>
                <Chip label={getFilteredStructuredServices().length} size="small" color="secondary" />
              </Stack>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredStructuredServices().map((service, index) => {
                  const serviceName = service?.label?.displayName || '';
                  const tracking = serviceTracking[serviceName];

                  // Display the creation date from activity tracking if available, otherwise use service timestamp
                  const displayDate = tracking?.createdAt || service?.timestamp;
                  const displayDateFormatted = displayDate ? new Date(displayDate).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '-';

                  return (
                  <TableRow key={`structured-${index}`} hover>
                    <TableCell>
                      <Chip
                        label={getDefinedServiceDisplayName(service?.serviceTypeId) ||  formatDisplayName(service?.serviceTypeId) }
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell>
                      {service?.description ? (
                        <>
                          {service?.label?.displayName && <strong>{service.label.displayName}</strong>}
                          {service?.label?.displayName && service?.label?.description && ' No description'}
                          {service?.description}
                        </>
                      ) : (
                        service?.label?.displayName ? <strong>{service?.label?.displayName}</strong> : ' No description'
                      )}
                    </TableCell>
                    <TableCell>
                      {displayDateFormatted}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(index, service?.description, service?.label?.displayName || '', 'structured')}
                        disabled={loading}
                        title="Edit description"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

{
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(index, 'structured', service?.label?.displayName || 'Unknown Service', service)}
                        disabled={loading}
                        title="Delete service"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                }
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}


  {  /*   {categories?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <BuildIcon />
                <Typography variant="h6">Categories</Typography>
                <Chip label={categories.length} size="small" color="secondary" />
              </Stack>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service Type</strong></TableCell>
                 
                </TableRow>
              </TableHead>
              <TableBody>
                {services.categories.map((cat, index) => (
                  <TableRow key={`structured-${index}`} hover>
                    <TableCell>
                      <Chip 
                        label={cat} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </TableCell>
                   
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )} */ }

      {!hasServices && !loading && (
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <WarningIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Keine Services gefunden
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Dieses Unternehmen hat noch keine Services konfiguriert
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Fügen Sie Ihren ersten Service hinzu
          </Button>
        </Card>
      )}

      {/* Add Service Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => !loading && setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon />
            <Typography variant="h6">Neuen Service hinzufügen</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Service Type Selector */}
            <FormControl fullWidth>
              <InputLabel>Service Type *</InputLabel>
              <Select
                value={serviceData.type}
                label="Service Type *"
                onChange={(e) => {
                  const newType = e.target.value;
                  setServiceData({
                    ...serviceData,
                    type: newType,
                    category: '',
                    serviceTypeId: '',
                    displayName: '',
                    description: ''
                  });
                }}
                disabled={loading}
              >
                <MenuItem value="freeForm">
                  <span style={{ color: '#1976D2' }}>●</span>
                  <span style={{ marginLeft: '8px' }}>Free Form Service (Multiple per category)</span>
                </MenuItem>
                <MenuItem value="structured">
                  <span style={{ color: '#1976D2' }}>●</span>
                  <span style={{ marginLeft: '8px' }}>Structured Service (One per type)</span>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category/Service Type Selector */}
            <FormControl fullWidth>
              <InputLabel>
                {serviceData.type === 'freeForm' ? 'Category *' : 'Service Type *'}
              </InputLabel>
              <Select
                value={serviceData.type === 'freeForm' ? serviceData.category : serviceData.serviceTypeId}
                label={serviceData.type === 'freeForm' ? 'Category *' : 'Service Type *'}
                onChange={(e) => {
                  if (serviceData.type === 'freeForm') {
                    setServiceData({...serviceData, category: e.target.value});
                  } else {
                    setServiceData({...serviceData, serviceTypeId: e.target.value});
                  }
                }}
                disabled={loading}
              >
                {categoriesForDropdown && categoriesForDropdown.length > 0 ? (
                  categoriesForDropdown
                    .filter(cat => cat.type === serviceData.type)
                    .map((cat) => (
                      <MenuItem
                        key={cat.value}
                        value={cat.value}
                        sx={{
                          backgroundColor: '#E3F2FD',
                          '&:hover': {
                            backgroundColor: '#BBDEFB'
                          }
                        }}
                      >
                        <span style={{
                          color: '#1976D2',
                          fontWeight: 'bold',
                          marginRight: '8px',
                          fontSize: '12px'
                        }}>
                          ●
                        </span>
                        {cat.type === 'freeForm' ? getServiceDisplayName(cat.value) : cat.label}
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: '#1976D2',
                          opacity: 0.7
                        }}>
                          ({cat.type === 'freeForm' ? 'Free Form' : 'Structured'})
                        </span>
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>
                    {serviceData.type === 'freeForm'
                      ? 'No free-form categories available'
                      : 'No unused service types available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Display Name - required for freeForm, optional for structured */}
            <TextField
              fullWidth
              label={serviceData.type === 'freeForm' ? 'Service Name *' : 'Service Name (Optional)'}
              value={serviceData.displayName}
              onChange={(e) => setServiceData({...serviceData, displayName: e.target.value})}
              placeholder={serviceData.type === 'freeForm' ? 'e.g., Wall Painting Service' : 'e.g., Standard Painting'}
              disabled={loading}
              required={serviceData.type === 'freeForm'}
            />

            {/* Description - optional for both */}
            <TextField
              fullWidth
              label="Description (Optional)"
              multiline
              rows={3}
              value={serviceData.description}
              onChange={(e) => setServiceData({...serviceData, description: e.target.value})}
              placeholder="Describe this service..."
              disabled={loading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAddDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createService}
            disabled={
              loading ||
              (serviceData.type === 'freeForm' && (!serviceData.category || !serviceData.displayName)) ||
              (serviceData.type === 'structured' && !serviceData.serviceTypeId)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Creating...' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Service Description Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => !loading && setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EditIcon />
            <Typography variant="h6">Edit Service Description</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {editingServiceName && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Service: {editingServiceName}
                </Typography>
              </Box>
            )}

            {aiSuggestion && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#E8F5E9',
                  border: '1px solid #4CAF50',
                  borderRadius: 1
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2E7D32' }}>
                  💡 AI Suggestion:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#1B5E20' }}>
                  {aiSuggestion}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditDescription(aiSuggestion)}
                  sx={{ color: '#2E7D32', borderColor: '#4CAF50' }}
                >
                  Use This Description
                </Button>
              </Box>
            )}

            {loadingSuggestion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: '#F5F5F5', borderRadius: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Generating AI suggestion...</Typography>
              </Box>
            )}

            {editingServiceName && !aiSuggestion && !loadingSuggestion && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={() => generateAiDescriptionSuggestion(editingServiceName)}
                sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
              >
                Generate AI Suggestion
              </Button>
            )}

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Enter service description..."
              disabled={loading}
              helperText={editDescription.length > 0 ? `${editDescription.length} characters` : ''}
            />

            {/* Add Service Creation Date Option */}
            <Box sx={{ p: 2, bgcolor: '#FFF9C4', border: '1px solid #FBC02D', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={addServiceActivityDate}
                    onChange={(e) => setAddServiceActivityDate(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Add Service Creation Date to Activity"
              />
              {addServiceActivityDate && (
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Service Creation Date & Time"
                  value={serviceActivityDate}
                  onChange={(e) => setServiceActivityDate(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2 }}
                />
              )}
              {addServiceActivityDate && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#F57F17' }}>
                  ✓ This will add the service to activity tracking and the background will turn green to blue.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setEditingServiceIndex(null);
              setEditingServiceType(null);
              setEditDescription('');
              setAddServiceActivityDate(false);
              setServiceActivityDate('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEditedDescription}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Service Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
          <DeleteIcon />
          Service löschen?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
              </Typography>
              <Typography variant="caption">
                Der Service wird dauerhaft gelöscht.
              </Typography>
            </Alert>

            <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828', mb: 1 }}>
                Service zum Löschen:
              </Typography>
              <Typography variant="body1" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                {deleteServiceData.name}
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary">
              Möchten Sie diesen Service wirklich löschen?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1 }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ flex: 1 }}
          >
            {loading ? 'Wird gelöscht...' : 'Ja, löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      {/* Service Generator Wizard */}
      <ServiceGeneratorWizard
        open={openGeneratorWizard}
        onClose={() => setOpenGeneratorWizard(false)}
        selectedBusiness={urlLocationId ? { ...selectedBusiness, googleLocationId: urlLocationId, id: urlLocationId } : selectedBusiness}
        onServicesGenerated={fetchServices}
      />
    </Box>
  );
};

export default ServiceManager;